import axios from 'axios';
import { KrakenBalance, KrakenTicker, KrakenPosition, KrakenAccountBalance } from '../types/kraken';
import { auth } from '../config/firebase';

interface KrakenServiceConfig {
  apiKey: string;
  privateKey: string;
}

class KrakenService {
  // Updated to use environment variable with fallback
  private readonly baseUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/kraken`;
  private config: KrakenServiceConfig | null = null;

  constructor(config?: KrakenServiceConfig) {
    if (config) {
      this.initialize(config);
    }
  }

  initialize(config: KrakenServiceConfig) {
    try {
      this.config = config;
      console.log('Initializing Kraken service');
    } catch (error) {
      console.error('Error initializing Kraken service:', error);
      throw error;
    }
  }

  private getErrorMessage(error: unknown): string {
    if (axios.isAxiosError(error)) {
      const axiosError = error;
      
      // Check for network errors
      if (!axiosError.response) {
        return 'Network error: Unable to connect to the server. Please check if the server is running.';
      }
      
      // Check for specific status codes
      switch (axiosError.response.status) {
        case 401:
          return 'Authentication failed: Invalid API key or secret. Please check your credentials.';
        case 403:
          return 'Access denied: Your API key does not have permission to perform this action.';
        case 404:
          return 'Resource not found: The requested endpoint does not exist.';
        case 429:
          return 'Rate limit exceeded: Too many requests. Please try again later.';
        case 500:
        case 502:
        case 503:
        case 504:
          return 'Server error: The Kraken API server is experiencing issues. Please try again later.';
      }
      
      // Try to extract error message from response
      if (axiosError.response.data) {
        if (typeof axiosError.response.data === 'string') {
          return axiosError.response.data;
        }
        
        if (typeof axiosError.response.data === 'object') {
          const data = axiosError.response.data as any;
          if (data.error) return data.error;
          if (data.message) return data.message;
        }
      }
      
      return `API error (${axiosError.response.status}): ${axiosError.message}`;
    }
    
    if (error instanceof Error) {
      return error.message;
    }
    
    return String(error);
  }

  async testConnection(): Promise<boolean> {
    try {
      if (!this.config) {
        throw new Error('Kraken service not initialized');
      }

      const response = await axios.post(`${this.baseUrl}/configure`, {
        apiKey: this.config.apiKey,
        apiSecret: this.config.privateKey,
        userId: auth.currentUser?.uid || 'test-user'
      });

      return response.data.status === 'ok';
    } catch (error) {
      console.error('Error testing Kraken connection:', error);
      throw new Error(this.getErrorMessage(error));
    }
  }

  async getAccountBalance(): Promise<KrakenAccountBalance> {
    try {
      if (!this.config) {
        throw new Error('Kraken service not initialized');
      }

      console.log('Fetching Kraken account balance with config:', {
        apiKey: this.config.apiKey ? '***' : 'undefined', 
        apiSecret: this.config.privateKey ? '***' : 'undefined'
      });

      // Use the correct endpoint that matches the server implementation
      const userId = auth.currentUser?.uid || 'test-user';
      const response = await axios.get(`${this.baseUrl}/balance/${userId}`);
      
      console.log('Kraken account balance response:', response.data);
      
      return response.data;
    } catch (error) {
      console.error('Error getting Kraken account balance:', error);
      throw new Error(this.getErrorMessage(error));
    }
  }

  async getOpenOrders(): Promise<any> {
    try {
      if (!this.config) {
        throw new Error('Kraken service not initialized');
      }

      const response = await axios.post(`${this.baseUrl}/orders/open`, {
        apiKey: this.config.apiKey,
        apiSecret: this.config.privateKey
      });
      
      return response.data;
    } catch (error) {
      console.error('Error getting Kraken open orders:', error);
      throw new Error(this.getErrorMessage(error));
    }
  }
}

export const krakenService = {
  createInstance: (config: KrakenServiceConfig) => {
    return new KrakenService(config);
  }
};
